/**
 * Mega-menu accessibility.
 *
 * Envy's Menu class tracks open state with a single class — `nav-hover` on the
 * parent <li> — and never tells assistive technology about it. This keeps
 * aria-expanded in step with that class, and adds the Escape key, which the
 * desktop menu had no handler for.
 *
 * It lives here rather than in the theme's JS because layout/theme.liquid loads
 * theme.min.js — 553 KB on a single line — and an edit there could not be
 * reviewed in a diff. Observing the class the vendor already sets means no
 * vendor behaviour is duplicated or overridden: this file only reports state,
 * it never changes it.
 */
/**
 * Input modality — `data-input="pointer"` or `"keyboard"` on <html>.
 *
 * This exists because of one specific, and slightly counter-intuitive, fact:
 * `:focus-visible` ALWAYS matches on a text input, however focus arrived. That is
 * the specification, not a browser quirk — you are about to type, so the browser
 * shows the ring.
 *
 * v2.1 tried to stop the search field ringing on every click by moving from
 * `:focus-within` to `:focus-visible`. It could never have worked, and Sebo kept
 * seeing the green ring: the search drawer focuses its input the moment it opens,
 * and a text input with focus matches `:focus-visible` regardless.
 *
 * So the distinction has to be made where the information actually is — in how the
 * user is driving. CSS reads this to suppress the ring for pointer users while
 * keeping it for keyboard users, who genuinely need it.
 */
(() => {
  const root = document.documentElement;
  const set = (mode) => root.setAttribute('data-input', mode);
  set('pointer');
  document.addEventListener('pointerdown', () => set('pointer'), { capture: true, passive: true });
  document.addEventListener('keydown', (evt) => {
    // Only navigation keys imply keyboard driving. Typing into a field does not:
    // the caret is already the affordance there.
    if (evt.key === 'Tab' || evt.key.startsWith('Arrow') || evt.key === 'Escape') set('keyboard');
  }, { capture: true });
})();

(() => {
  const nav = document.querySelector('#AccessibleNav');
  if (!nav) return;

  const parents = () => nav.querySelectorAll(':scope > li.site-nav--has-dropdown');

  const sync = (li) => {
    const link = li.querySelector(':scope > a.site-nav__link');
    if (!link) return;
    link.setAttribute('aria-expanded', li.classList.contains('nav-hover') ? 'true' : 'false');
  };

  parents().forEach((li) => {
    sync(li);
    new MutationObserver(() => sync(li)).observe(li, {
      attributes: true,
      attributeFilter: ['class'],
    });
  });

  // Escape closes the open panel and returns focus to its trigger, which is
  // where a keyboard user expects to be left.
  document.addEventListener('keydown', (evt) => {
    if (evt.key !== 'Escape') return;
    const open = nav.querySelector(':scope > li.nav-hover');
    if (!open) return;
    const link = open.querySelector(':scope > a.site-nav__link');
    open.classList.remove('nav-hover');
    sync(open);
    if (link) link.focus();
  });

  /**
   * Menu imagery loads on interest, not on arrival.
   *
   * MEASURED on the live homepage: 28 images, 1,068 KB — 24% of the page's total
   * 4.4 MB — downloaded on every page load, for six panels nobody had opened.
   * The images already carry loading="lazy"; it does nothing, because a closed
   * panel is `display: block; visibility: hidden`, 420px tall, sitting inside the
   * viewport. Lazy loading asks whether an element is near the viewport, not
   * whether anyone can see it, and the answer here is yes.
   *
   * The CSS gives closed panels `content-visibility: hidden` until this class
   * lands, which takes their contents out of rendering, so the lazy images hold.
   * Entering the navigation at all releases them — and that happens before any
   * panel opens, since Envy runs the panel behind a hover-intent delay. Someone
   * who never touches the menu never pays for it.
   *
   * `once` on every listener: this runs one time and then the header behaves
   * exactly as it did before.
   */
  /**
   * The mobile drawer — the same defect as the desktop nav, measured the same way.
   *
   * The hamburger ships with aria-controls="NavDrawer" and aria-expanded="false",
   * and the "false" never changes. MEASURED: drawer open at x:0 and drawer closed
   * at x:-767 both report aria-expanded="false". The state is tracked the whole
   * time, as `js-drawer-open-left` on <body> — it is simply never reported, so a
   * screen reader user is told the menu is shut while looking at it.
   *
   * Same approach as above: observe the class the vendor already sets. Nothing
   * here opens or closes anything.
   */
  const hamburger = document.querySelector('.hamburger-nav-button');
  if (hamburger) {
    const syncDrawer = () => {
      hamburger.setAttribute(
        'aria-expanded',
        document.body.classList.contains('js-drawer-open-left') ? 'true' : 'false'
      );
    };
    syncDrawer();
    new MutationObserver(syncDrawer).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * The drawer's section toggles.
   *
   * Each section has two buttons — one to open, one to close — and only ever one
   * of them is displayed, so only one reaches assistive technology at a time.
   * That part is sound. What is missing is any statement of state, and any hint
   * of what is being expanded: eight buttons, all announcing "See more".
   *
   * Both are fixed from the section's own link text and the `mobile-nav--expanded`
   * class the vendor toggles.
   */
  document.querySelectorAll('.mobile-nav__has-sublist').forEach((holder) => {
    const link = holder.querySelector(':scope > .mobile-nav__link');
    const name = link ? link.textContent.trim() : '';
    const buttons = holder.querySelectorAll(':scope > .mobile-nav__toggle button');
    if (!buttons.length) return;

    const syncSection = () => {
      const expanded = holder.classList.contains('mobile-nav--expanded');
      buttons.forEach((button) => {
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (!name) return;
        // "See more" eight times over says nothing. The section's own name is
        // right there in the sibling link.
        const base = button.className.includes('toggle-close') ? 'Collapse' : 'Expand';
        button.setAttribute('aria-label', `${base} ${name}`);
      });
    };
    syncSection();
    new MutationObserver(syncSection).observe(holder, {
      attributes: true,
      attributeFilter: ['class'],
    });
  });

  const header = document.querySelector('.site-header');
  if (header) {
    const warm = () => {
      header.classList.add('menu-warmed');
      // Releasing content-visibility is not enough on its own — MEASURED: the
      // panel opened with every photograph missing and stayed that way. Taking an
      // element out of `content-visibility: hidden` does not restart lazy loading
      // for images that were skipped while it was hidden, so the load has to be
      // asked for. Switching to eager does exactly that, and only for images that
      // never loaded.
      header.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    };
    const opts = { once: true, passive: true };

    // Pointer entering the nav — the ordinary desktop route, and the earliest
    // honest signal of intent.
    nav.addEventListener('pointerenter', warm, opts);
    // Keyboard: tabbing into the header.
    header.addEventListener('focusin', warm, { once: true });
    // Touch: the drawer toggle lives in the header, not the nav.
    header.addEventListener('touchstart', warm, opts);
    // A pointer moving anywhere across the header is already close enough.
    header.addEventListener('pointerover', warm, opts);
  }
})();
